import api from '@/services/api';
import { PaginatedResponse } from '@/types/common';
import {
  Partner,
  CreatePartnerRequest,
  PartnerQueryParams,
  UpdatePartnerRequest,
  PartnerEmail,
  PartnerEmailsParams,
  UpdatePartnerEmailRequest,
} from '@/types/partner';

export const partnerService = {
  async getAll(params?: PartnerQueryParams): Promise<PaginatedResponse<Partner>> {
    const queryParams = new URLSearchParams();
    const offset = params?.offset ?? 0;
    const limit = params?.limit ?? 1000;
    queryParams.append('offset', offset.toString());
    queryParams.append('limit', limit.toString());
    if (params?.q) {
      queryParams.append('q', params.q);
    }
    const url = `/partners?${queryParams.toString()}`;
    const response = await api.get<Partner[] | PaginatedResponse<Partner>>(url);
    const body = response.data;
    if (Array.isArray(body)) {
      return { data: body, total: body.length, remaining: 0 };
    }
    return {
      data: Array.isArray(body?.data) ? body.data : [],
      total: body?.total ?? 0,
      remaining: body?.remaining ?? 0,
    };
  },

  async create(data: CreatePartnerRequest): Promise<Partner> {
    const response = await api.post<Partner>('/partners', data);
    return response.data;
  },

  async update(id: string, data: UpdatePartnerRequest): Promise<Partner> {
    const response = await api.patch<Partner>(`/partners/${id}`, data);
    return response.data;
  },

  async getUsers(
    partnerId: string,
    params?: PartnerEmailsParams
  ): Promise<PaginatedResponse<PartnerEmail>> {
    const queryParams = new URLSearchParams();
    queryParams.append('offset', String(params?.offset ?? 0));
    queryParams.append('limit', String(params?.limit ?? 10));
    if (params?.q) queryParams.append('q', params.q);
    const url = `/partners/${partnerId}/users?${queryParams.toString()}`;
    const response = await api.get<PartnerEmail[] | PaginatedResponse<PartnerEmail>>(url);
    const body = response.data;
    if (Array.isArray(body)) {
      return { data: body, total: body.length, remaining: 0 };
    }
    return body;
  },

  async addUsers(partnerId: string, emails: string[]): Promise<void> {
    await api.post(`/partners/${partnerId}/users`, { emails });
  },

  async updateUser(
    partnerId: string,
    userId: string,
    data: UpdatePartnerEmailRequest
  ): Promise<PartnerEmail> {
    const response = await api.patch<PartnerEmail>(
      `/partners/${partnerId}/users/${userId}`,
      data
    );
    return response.data;
  },

  async removeUser(partnerId: string, userId: string): Promise<void> {
    await api.delete(`/partners/${partnerId}/users/${userId}`);
  },
};
